/**
 * Ticket SK2: Arbeitsvorrat der Kaufstrecke.
 *
 * Zwei Blöcke: oben die Auffälligkeiten, die eine Person ansehen muss (Zahlung
 * ohne Zuordnung, abgewiesene Signatur, Erstattung, Anschrift außerhalb
 * Deutschlands), unten die Liste der Käufe. Es gibt hier keine Möglichkeit,
 * einen Zustand zu setzen — der Zustand entsteht ausschließlich aus den
 * geprüften Rückmeldungen des Zahlungsdienstes. Einzige Ausnahme (Ticket SK3):
 * eine gescheiterte Freischaltung lässt sich von Hand nachholen.
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { AlertTriangle, Inbox, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  acknowledgePurchaseDecisionFn,
  getCheckoutReadiness,
  listPurchaseAlerts,
  retryPurchaseFulfillmentFn,
  retryPurchaseInvoiceFn,
  listPurchases,
} from "@/lib/purchases.functions";

import { warmAdminQuery } from "@/lib/admin-loader";
import {
  PURCHASE_FULFILLMENT_LABELS,
  PURCHASE_MOCO_LABELS,
  PURCHASE_STATUS_LABELS,
  type PurchaseStatus,
} from "@/lib/purchases.shared";

type PurchaseView = "freischaltung" | "rechnung" | "entscheidung";

const VIEW_LABELS: Record<PurchaseView, string> = {
  freischaltung: "Ohne Freischaltung",
  entscheidung: "Offene Entscheidung",
  rechnung: "Ohne Rechnung",
};

export const Route = createFileRoute("/_admin/admin/purchases")({
  // Ticket SK11: Sprungziel des Arbeitsvorrats.
  validateSearch: (search: Record<string, unknown>) => {
    const raw = typeof search["sicht"] === "string" ? (search["sicht"] as string) : "";
    return {
      sicht: (raw in VIEW_LABELS ? raw : undefined) as PurchaseView | undefined,
    };
  },
  loader: ({ context }) => {
    warmAdminQuery(context.queryClient, {
      queryKey: ["admin-purchases", "", "", ""],
      queryFn: () => listPurchases({ data: { status: null, mode: null, view: null } }),
    });
  },
  component: PurchasesPage,
});


const selectClass =
  "rounded-sm border border-foreground/20 bg-background px-3 py-2 font-body text-sm text-foreground focus:border-primary focus:outline-none";

function formatDate(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat("de-DE", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Europe/Berlin",
  }).format(d);
}

function euro(cents: number | null): string {
  if (cents === null) return "—";
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(
    cents / 100,
  );
}

const STATUS_ORDER: PurchaseStatus[] = [
  "angelegt",
  "bezahlt",
  "abgebrochen",
  "fehlgeschlagen",
  "erstattet",
];

function PurchasesPage() {
  const listFn = useServerFn(listPurchases);
  const alertsFn = useServerFn(listPurchaseAlerts);
  const readinessFn = useServerFn(getCheckoutReadiness);
  const retryFn = useServerFn(retryPurchaseFulfillmentFn);
  const retryInvoiceFn = useServerFn(retryPurchaseInvoiceFn);
  const acknowledgeFn = useServerFn(acknowledgePurchaseDecisionFn);
  const queryClient = useQueryClient();
  /** Ticket ER1: Bemerkung je Kauf, nur solange die Karte offen ist. */
  const [decisionNote, setDecisionNote] = useState<Record<string, string>>({});


  // Ticket SK3: Nachholen von Hand. Der Kauf bleibt bezahlt; nur die
  // Freischaltung wird erneut versucht.
  const retry = useMutation({
    mutationFn: (purchaseId: string) => retryFn({ data: { purchaseId } }),
    onSuccess: (result) => {
      toast[result.ok ? "success" : "error"](
        result.ok
          ? "Freischaltung nachgeholt."
          : `Freischaltung erneut fehlgeschlagen: ${result.note}`,
      );
      void queryClient.invalidateQueries({ queryKey: ["admin-purchases"] });
      void queryClient.invalidateQueries({ queryKey: ["admin-purchase-alerts"] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Nachholen fehlgeschlagen."),
  });

  // Ticket SK4: Rechnung erneut versuchen. Erzeugt keine zweite Rechnung.
  const retryInvoice = useMutation({
    mutationFn: (purchaseId: string) => retryInvoiceFn({ data: { purchaseId } }),
    onSuccess: (result) => {
      toast[result.ok ? "success" : "error"](result.note);
      void queryClient.invalidateQueries({ queryKey: ["admin-purchases"] });
      void queryClient.invalidateQueries({ queryKey: ["admin-purchase-alerts"] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Rechnungslauf fehlgeschlagen."),
  });

  /**
   * Ticket ER1: Entscheidung „Zugang bleibt bestehen" quittieren. Für einen
   * erstatteten Kauf mit bereits erledigter Freischaltung ist das der einzige
   * Ausgang aus dem Arbeitsvorrat: Der Statuswächter kennt keinen Weg von
   * „erledigt" nach „entfällt". Deshalb wird hier auch kein Schalter angeboten,
   * der die Freischaltung umschreiben würde.
   */
  const acknowledgeDecision = useMutation({
    mutationFn: (vars: { purchaseId: string; note: string }) =>
      acknowledgeFn({
        data: { purchaseId: vars.purchaseId, note: vars.note.trim() || undefined },
      }),
    onSuccess: (result) => {
      toast[result.acknowledged ? "success" : "error"](result.note);
      setDecisionNote({});
      void queryClient.invalidateQueries({ queryKey: ["admin-purchases"] });
      void queryClient.invalidateQueries({ queryKey: ["admin-purchase-alerts"] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Quittierung fehlgeschlagen."),
  });


  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [status, setStatus] = useState<PurchaseStatus | "">("");
  const [mode, setMode] = useState<"test" | "live" | "">("");
  const view = search.sicht ?? "";

  const purchases = useQuery({
    queryKey: ["admin-purchases", status, mode, view],
    queryFn: () =>
      listFn({ data: { status: status || null, mode: mode || null, view: view || null } }),
  });

  const alerts = useQuery({ queryKey: ["admin-purchase-alerts"], queryFn: () => alertsFn() });
  const readiness = useQuery({
    queryKey: ["admin-checkout-readiness"],
    queryFn: () => readinessFn(),
  });

  return (
    <div className="space-y-8 p-6 md:p-8">
      <header className="space-y-2">
        <h1 className="font-display text-2xl font-extrabold text-primary">Käufe</h1>
        <p className="max-w-[70ch] font-body text-sm text-muted-foreground">
          Alle Käufe aus der Kaufstrecke. Der Zustand wird ausschließlich durch geprüfte
          Rückmeldungen des Zahlungsdienstes gesetzt; hier wird nur gelesen.
        </p>
      </header>

      {readiness.data && (
        <div className="rounded-sm border border-foreground/15 p-5 font-body text-sm">
          <p className="font-display text-base font-extrabold text-primary">
            Betriebsart: {readiness.data.mode === "live" ? "Echtbetrieb" : "Testbetrieb"}
          </p>
          <ul className="mt-3 space-y-1 text-muted-foreground">
            <li>
              Zugangsdaten der aktiven Betriebsart:{" "}
              {readiness.data.activeReady ? "vollständig" : "unvollständig"}
              {readiness.data.activeMessage ? ` — ${readiness.data.activeMessage}` : ""}
            </li>
            <li>
              Testbetrieb: Schlüssel {readiness.data.testSecretKeyReady ? "ja" : "nein"}, Signatur{" "}
              {readiness.data.testWebhookSecretReady ? "ja" : "nein"}
            </li>
            <li>
              Echtbetrieb: Schlüssel {readiness.data.liveSecretKeyReady ? "ja" : "nein"}, Signatur{" "}
              {readiness.data.liveWebhookSecretReady ? "ja" : "nein"}
            </li>
          </ul>
        </div>
      )}

      <section className="space-y-3">
        <h2 className="font-display text-lg font-extrabold text-primary">Auffälligkeiten</h2>
        {alerts.isPending && (
          <p className="flex items-center gap-2 font-body text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Wird geladen …
          </p>
        )}
        {alerts.data && alerts.data.length === 0 && (
          <p className="font-body text-sm text-muted-foreground">
            Keine offenen Auffälligkeiten.
          </p>
        )}
        {alerts.data && alerts.data.length > 0 && (
          <ul className="space-y-2">
            {alerts.data.map((a) => (
              <li
                key={a.id}
                className="flex items-start gap-3 rounded-sm border border-destructive/30 bg-destructive/5 p-4 font-body text-sm"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden />
                <span>
                  <span className="font-display font-extrabold text-foreground">{a.label}</span>
                  {a.detail ? ` — ${a.detail}` : ""}
                  <span className="block text-muted-foreground">{formatDate(a.created_at)}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="flex flex-wrap gap-3">
        <select
          aria-label="Status"
          className={selectClass}
          value={status}
          onChange={(e) => setStatus(e.target.value as PurchaseStatus | "")}
        >
          <option value="">Alle Status</option>
          {STATUS_ORDER.map((s) => (
            <option key={s} value={s}>
              {PURCHASE_STATUS_LABELS[s]}
            </option>
          ))}
        </select>
        <select
          aria-label="Betriebsart"
          className={selectClass}
          value={mode}
          onChange={(e) => setMode(e.target.value as "test" | "live" | "")}
        >
          <option value="">Test und Echt</option>
          <option value="test">Testbetrieb</option>
          <option value="live">Echtbetrieb</option>
        </select>
        <select
          aria-label="Sicht"
          className={selectClass}
          value={view}
          onChange={(e) => {
            const next = e.target.value as PurchaseView | "";
            void navigate({ search: { sicht: next || undefined }, replace: true });
          }}
        >
          <option value="">Alle Käufe</option>
          {(Object.keys(VIEW_LABELS) as PurchaseView[]).map((v) => (
            <option key={v} value={v}>
              {VIEW_LABELS[v]}
            </option>
          ))}
        </select>
      </div>


      {purchases.isPending && (
        <p className="flex items-center gap-2 font-body text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Wird geladen …
        </p>
      )}
      {purchases.isError && (
        <p className="font-body text-sm text-destructive">
          {(purchases.error as Error)?.message ?? "Käufe konnten nicht geladen werden."}
        </p>
      )}
      {purchases.data && purchases.data.length === 0 && (
        <div className="flex items-center gap-3 rounded-sm border border-foreground/15 p-6 font-body text-sm text-muted-foreground">
          <Inbox className="h-5 w-5 shrink-0" aria-hidden />
          {view ? "In dieser Sicht ist nichts offen." : "Noch keine Käufe vorhanden."}
        </div>
      )}


      {purchases.data && purchases.data.length > 0 && (
        <ul className="space-y-4">
          {purchases.data.map((row) => (
            <li key={row.id} className="rounded-sm border border-foreground/15 p-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="font-display text-base font-extrabold text-foreground">
                    {row.service_title}
                    {row.course_title ? ` · ${row.course_title}` : ""}
                  </p>
                  <p className="mt-1 font-body text-sm text-muted-foreground">
                    {formatDate(row.created_at)} · {euro(row.amount_cents)} ·{" "}
                    {row.mode === "live" ? "Echtbetrieb" : "Testbetrieb"}
                  </p>
                  <p className="mt-2 font-body text-sm text-foreground">
                    {/* Ticket SK14: Vor bestätigter Zahlung liegen noch keine
                        Angaben vor — das ist der Normalfall, kein Datenverlust. */}
                    {row.billing_name ??
                      `${row.first_name ?? ""} ${row.last_name ?? ""}`.trim() ??
                      ""}
                    {row.organisation ? ` · ${row.organisation}` : ""}
                    {row.email ? (
                      <>
                        {" · "}
                        <a
                          href={`mailto:${row.email}`}
                          className="underline underline-offset-2 hover:opacity-80"
                        >
                          {row.email}
                        </a>
                      </>
                    ) : (
                      " · Angaben noch offen (keine bestätigte Zahlung)"
                    )}
                  </p>

                  {row.billing_country && (
                    <p className="mt-1 font-body text-sm text-muted-foreground">
                      Anschrift: {row.billing_line1 ?? ""} {row.billing_postal_code ?? ""}{" "}
                      {row.billing_city ?? ""} ({row.billing_country})
                    </p>
                  )}
                  {row.tax_amount_cents !== null && (
                    <p className="mt-1 font-body text-sm text-muted-foreground">
                      enthaltene Steuer: {euro(row.tax_amount_cents)}
                    </p>
                  )}
                  {row.fulfillment_error && (
                    <p className="mt-2 font-body text-sm text-destructive">
                      Freischaltung fehlgeschlagen: {row.fulfillment_error}
                    </p>
                  )}
                  {row.moco_error && (
                    <p className="mt-2 font-body text-sm text-destructive">
                      {row.moco_status === "rundungsabweichung"
                        ? "Rundungsabweichung — Zahlung wurde nicht gebucht: "
                        : "Rechnung: "}
                      {row.moco_error}
                      {row.moco_gross_reported_cents !== null && (
                        <>
                          {" "}
                          (Moco: {euro(row.moco_gross_reported_cents)}, bezahlt:{" "}
                          {euro(row.amount_cents)})
                        </>
                      )}
                    </p>
                  )}
                  {row.moco_credit_note_required && (
                    <p className="mt-2 font-body text-sm text-destructive">
                      In Moco ist eine Gutschrift zu erstellen. Es wurde bewusst keine
                      Storno-Rechnung erzeugt.
                    </p>
                  )}
                  {row.moco_status === "probelauf" && row.moco_dry_run_payload && (
                    <details className="mt-2">
                      <summary className="cursor-pointer font-display text-xs font-extrabold text-primary">
                        Probelauf: Rechnung, die im Echtbetrieb entstanden wäre
                      </summary>
                      <pre className="mt-2 max-h-64 overflow-auto rounded-sm bg-foreground/5 p-3 font-body text-xs text-muted-foreground">
                        {JSON.stringify(row.moco_dry_run_payload, null, 2)}
                      </pre>
                    </details>
                  )}
                  {row.internal_note && (
                    <p className="mt-2 font-body text-sm text-destructive">{row.internal_note}</p>
                  )}
                  {/* Ticket ER1: Entscheidung bei Erstattungen. Quittiert
                      bedeutet „Zugang bleibt bestehen"; der Vorgang verlässt
                      den Arbeitsvorrat und bleibt hier sichtbar. */}
                  {row.decision_acknowledged_at ? (
                    <p className="mt-2 font-body text-sm text-muted-foreground">
                      Zugang bleibt bestehen — entschieden am{" "}
                      {formatDate(row.decision_acknowledged_at)}
                      {row.decision_acknowledged_by_name
                        ? ` von ${row.decision_acknowledged_by_name}`
                        : ""}
                      {row.decision_acknowledged_note
                        ? `: ${row.decision_acknowledged_note}`
                        : ""}
                    </p>
                  ) : (
                    row.status === "erstattet" &&
                    row.fulfillment_status !== "entfaellt" && (
                      <div className="mt-3 rounded-sm border border-foreground/15 p-3">
                        <p className="font-display text-xs font-extrabold text-foreground">
                          Entscheidung über den Zugang
                        </p>
                        <p className="mt-1 font-body text-xs text-muted-foreground">
                          Soll der Zugang bestehen bleiben, quittieren Sie das hier. Wird der
                          Zugang entzogen, geschieht das in der Kundenakte — nicht an diesem
                          Kauf. Dort ist der Unterschied groß: Die Kurszuweisung zu entfernen
                          nimmt der Person genau diesen einen Kurs weg. Die Berechtigung zu
                          deaktivieren beendet dagegen den gesamten Cockpit-Zugang, also auch
                          alles, was die Person bezahlt hat und behalten darf.
                        </p>
                        <input
                          type="text"
                          value={decisionNote[row.id] ?? ""}
                          onChange={(e) =>
                            setDecisionNote((prev) => ({ ...prev, [row.id]: e.target.value }))
                          }
                          placeholder="Bemerkung (freiwillig)"
                          maxLength={500}
                          className="mt-2 w-full rounded-sm border border-foreground/20 bg-background px-2 py-1 font-body text-sm text-foreground focus:border-primary focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            acknowledgeDecision.mutate({
                              purchaseId: row.id,
                              note: decisionNote[row.id] ?? "",
                            })
                          }
                          disabled={acknowledgeDecision.isPending}
                          className="mt-2 inline-flex items-center gap-1 rounded-sm bg-brand-button px-2 py-1 font-display text-xs font-extrabold text-foreground disabled:opacity-60"
                        >
                          {acknowledgeDecision.isPending && (
                            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                          )}
                          Zugang bleibt bestehen
                        </button>
                      </div>
                    )
                  )}
                </div>
                <div className="text-right font-body text-xs text-muted-foreground">
                  <span className="inline-flex rounded-sm bg-foreground/5 px-2 py-0.5">
                    {PURCHASE_STATUS_LABELS[row.status as PurchaseStatus]}
                  </span>
                  <span className="mt-2 block">
                    Freischaltung:{" "}
                    {PURCHASE_FULFILLMENT_LABELS[
                      row.fulfillment_status as keyof typeof PURCHASE_FULFILLMENT_LABELS
                    ]}
                    {/* Ticket ER1: Diese Kennzeichnung erscheint nur, wo der
                        Zustand tatsächlich vorliegt. */}
                    {row.fulfillment_status === "entfaellt" ? " — Zugang entzogen" : ""}
                  </span>

                  <span className="mt-1 block">
                    Rechnung:{" "}
                    {PURCHASE_MOCO_LABELS[
                      row.moco_status as keyof typeof PURCHASE_MOCO_LABELS
                    ] ?? row.moco_status}
                    {row.moco_invoice_number ? ` (${row.moco_invoice_number})` : ""}
                  </span>
                  {row.paid_at && <span className="mt-1 block">bezahlt: {formatDate(row.paid_at)}</span>}
                  {(row.fulfillment_status === "fehlgeschlagen" ||
                    row.fulfillment_status === "in_arbeit") && (
                    <button
                      type="button"
                      onClick={() => retry.mutate(row.id)}
                      disabled={retry.isPending}
                      className="mt-2 inline-flex items-center gap-1 rounded-sm bg-brand-button px-2 py-1 font-display text-xs font-extrabold text-foreground disabled:opacity-60"
                    >
                      {retry.isPending && <Loader2 className="h-3 w-3 animate-spin" aria-hidden />}
                      Freischaltung nachholen
                    </button>
                  )}
                  {row.status === "bezahlt" &&
                    (row.moco_status === "fehlgeschlagen" ||
                      row.moco_status === "in_arbeit" ||
                      row.moco_status === "rundungsabweichung" ||
                      (row.moco_status === "angelegt" && row.moco_error)) && (
                      <button
                        type="button"
                        onClick={() => retryInvoice.mutate(row.id)}
                        disabled={retryInvoice.isPending}
                        className="mt-2 inline-flex items-center gap-1 rounded-sm bg-brand-button px-2 py-1 font-display text-xs font-extrabold text-foreground disabled:opacity-60"
                      >
                        {retryInvoice.isPending && (
                          <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                        )}
                        Rechnung erneut versuchen
                      </button>
                    )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
